import React, { useState } from 'react';
import { Trash2, Flag, Ban, AlertTriangle, Copy, Reply, Pencil } from 'lucide-react';
import { moderationService } from '../../services/moderationService';
import { useAuth } from '../../contexts/AuthContext';
import { useChat } from '../../contexts/ChatContext';

interface MessageContextMenuProps {
  messageId: string;
  messageContent: string;
  messageSenderId: string;
  isOwnMessage: boolean;
  isVisible: boolean;
  position: { x: number; y: number };
  onClose: () => void;
  onReply?: () => void;
  onDelete?: () => void;
  /** Só chega nas próprias mensagens; abre a edição na bolha. */
  onEdit?: () => void;
  /** Só chega nas próprias mensagens; abre a confirmação de exclusão. */
  onRequestDelete?: () => void;
}

interface BlockUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (reason: string, hours?: number) => void;
  userName: string;
}

interface ReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (reason: string, description: string) => void;
}

const BlockUserModal: React.FC<BlockUserModalProps> = ({ isOpen, onClose, onConfirm, userName }) => {
  const [reason, setReason] = useState('');
  const [duration, setDuration] = useState<string>('');
  const [customDuration, setCustomDuration] = useState('');

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!reason.trim()) return;

    let hours: number | undefined;
    if (duration === 'custom' && customDuration) {
      hours = parseInt(customDuration);
    } else if (duration && duration !== 'permanent') {
      hours = parseInt(duration);
    }

    onConfirm(reason, hours);
    setReason('');
    setDuration('');
    setCustomDuration('');
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
        <h3 className="text-lg font-semibold mb-4">Bloquear Usuário</h3>
        <p className="text-gray-600 mb-4">
          Tem certeza que deseja bloquear <strong>{userName}</strong>?
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Motivo do bloqueio *
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-coral"
              rows={3}
              placeholder="Descreva o motivo do bloqueio..."
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Duração do bloqueio
            </label>
            <select
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
              className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-coral"
            >
              <option value="">Selecione a duração</option>
              <option value="1">1 hora</option>
              <option value="24">24 horas</option>
              <option value="168">1 semana</option>
              <option value="720">1 mês</option>
              <option value="custom">Personalizado</option>
              <option value="permanent">Permanente</option>
            </select>
          </div>

          {duration === 'custom' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Horas
              </label>
              <input
                type="number"
                value={customDuration}
                onChange={(e) => setCustomDuration(e.target.value)}
                className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-coral"
                placeholder="Número de horas"
                min="1"
                max="8760"
              />
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary flex-1"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="btn-danger flex-1"
              disabled={!reason.trim()}
            >
              Bloquear
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const ReportModal: React.FC<ReportModalProps> = ({ isOpen, onClose, onConfirm }) => {
  const [reason, setReason] = useState('');
  const [description, setDescription] = useState('');

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!reason) return;

    onConfirm(reason, description);
    setReason('');
    setDescription('');
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
        <h3 className="text-lg font-semibold mb-4">Reportar Conteúdo</h3>
        <p className="text-gray-600 mb-4">
          Por que você está reportando este conteúdo?
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Motivo *
            </label>
            <select
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-coral"
              required
            >
              <option value="">Selecione um motivo</option>
              <option value="spam">Spam ou conteúdo irrelevante</option>
              <option value="harassment">Assédio ou bullying</option>
              <option value="hate_speech">Discurso de ódio</option>
              <option value="inappropriate">Conteúdo inadequado</option>
              <option value="violence">Violência ou ameaças</option>
              <option value="misinformation">Desinformação</option>
              <option value="other">Outro</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Descrição adicional (opcional)
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-coral"
              rows={3}
              placeholder="Forneça mais detalhes se necessário..."
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary flex-1"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="btn-danger flex-1"
              disabled={!reason}
            >
              Reportar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const MessageContextMenu: React.FC<MessageContextMenuProps> = ({
  messageId,
  messageContent,
  messageSenderId,
  isOwnMessage,
  isVisible,
  position,
  onClose,
  onReply,
  onDelete,
  onEdit,
  onRequestDelete
}) => {
  const { user } = useAuth();
  const { currentRoom, isCurrentUserAdmin } = useChat();
  const [isAdmin, setIsAdmin] = useState(false);
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  React.useEffect(() => {
    const checkAdminStatus = async () => {
      if (user && currentRoom) {
        const adminStatus = await isCurrentUserAdmin();
        setIsAdmin(adminStatus);
      }
    };
    checkAdminStatus();
    // Depende de `user?.id`, não do objeto `user` (ver App.tsx) — evita reverificar
    // toda vez que a aba volta a ficar visível.
  }, [user?.id, currentRoom, isCurrentUserAdmin]);

  if (!isVisible || !user) return null;

  const handleCopyMessage = () => {
    navigator.clipboard.writeText(messageContent);
    onClose();
  };

  const handleDeleteMessage = async () => {
    if (!isAdmin) return;

    setIsLoading(true);
    try {
      const success = await moderationService.deleteMessage(
        messageId,
        user.id,
        'Mensagem deletada pelo moderador'
      );
      
      if (success) {
        onDelete?.();
      }
    } catch (error) {
      console.error('Erro ao deletar mensagem:', error);
    } finally {
      setIsLoading(false);
      onClose();
    }
  };

  const handleBlockUser = async (reason: string, hours?: number) => {
    if (!isAdmin || !currentRoom) return;

    setIsLoading(true);
    try {
      const success = await moderationService.blockUser(
        currentRoom.id,
        messageSenderId,
        user.id,
        reason,
        hours
      );
      
      if (success) {
        console.log('Usuário bloqueado com sucesso');
      }
    } catch (error) {
      console.error('Erro ao bloquear usuário:', error);
    } finally {
      setIsLoading(false);
      setShowBlockModal(false);
      onClose();
    }
  };

  const handleReportContent = async (reason: string, description: string) => {
    setIsLoading(true);
    try {
      const success = await moderationService.reportContent(
        messageId,
        user.id,
        reason,
        description
      );
      
      if (success) {
        console.log('Conteúdo reportado com sucesso');
      }
    } catch (error) {
      console.error('Erro ao reportar conteúdo:', error);
    } finally {
      setIsLoading(false);
      setShowReportModal(false);
      onClose();
    }
  };

  return (
    <>
      <div
        className="fixed bg-white border border-gray-200 rounded-lg shadow-lg py-2 z-50 min-w-[180px]"
        style={{
          left: position.x,
          top: position.y,
          transform: position.x > window.innerWidth - 200 ? 'translateX(-100%)' : 'none'
        }}
      >
        {/* Responder */}
        {onReply && (
          <button
            onClick={() => {
              onReply();
              onClose();
            }}
            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
          >
            <Reply className="w-4 h-4" />
            Responder
          </button>
        )}

        {/* Copiar */}
        <button
          onClick={handleCopyMessage}
          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
        >
          <Copy className="w-4 h-4" />
          Copiar texto
        </button>

        {/* Editar e excluir a própria mensagem. Quem decide é o RLS: a tela
            só deixa de oferecer o que o banco negaria. */}
        {isOwnMessage && onEdit && (
          <button
            onClick={() => {
              onEdit();
              onClose();
            }}
            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
          >
            <Pencil className="w-4 h-4" />
            Editar mensagem
          </button>
        )}

        {isOwnMessage && onRequestDelete && (
          <button
            onClick={() => {
              onRequestDelete();
              onClose();
            }}
            className="w-full text-left px-4 py-2 text-sm text-nk-error hover:bg-red-50 flex items-center gap-2"
          >
            <Trash2 className="w-4 h-4" />
            Excluir mensagem
          </button>
        )}

        {/* Separador se houver ações de moderação */}
        {(isAdmin || !isOwnMessage) && (
          <div className="border-t border-gray-200 my-1" />
        )}

        {/* Deletar mensagem (apenas admins) */}
        {isAdmin && (
          <button
            onClick={handleDeleteMessage}
            disabled={isLoading}
            className="w-full text-left px-4 py-2 text-sm text-nk-error hover:bg-red-50 flex items-center gap-2 disabled:opacity-50"
          >
            <Trash2 className="w-4 h-4" />
            Deletar mensagem
          </button>
        )}

        {/* Bloquear usuário (apenas admins, não próprias mensagens) */}
        {isAdmin && !isOwnMessage && (
          <button
            onClick={() => {
              setShowBlockModal(true);
              onClose();
            }}
            disabled={isLoading}
            className="w-full text-left px-4 py-2 text-sm text-nk-error hover:bg-red-50 flex items-center gap-2 disabled:opacity-50"
          >
            <Ban className="w-4 h-4" />
            Bloquear usuário
          </button>
        )}

        {/* Reportar conteúdo (qualquer usuário, não próprias mensagens) */}
        {!isOwnMessage && (
          <button
            onClick={() => {
              setShowReportModal(true);
              onClose();
            }}
            disabled={isLoading}
            className="w-full text-left px-4 py-2 text-sm text-orange-600 hover:bg-orange-50 flex items-center gap-2 disabled:opacity-50"
          >
            <Flag className="w-4 h-4" />
            Reportar conteúdo
          </button>
        )}
      </div>

      {/* Overlay para fechar o menu ao clicar fora */}
      <div
        className="fixed inset-0 z-40"
        onClick={onClose}
      />

      {/* Modais */}
      <BlockUserModal
        isOpen={showBlockModal}
        onClose={() => setShowBlockModal(false)}
        onConfirm={handleBlockUser}
        userName="Usuário" // TODO: Passar o nome real do usuário
      />

      <ReportModal
        isOpen={showReportModal}
        onClose={() => setShowReportModal(false)}
        onConfirm={handleReportContent}
      />
    </>
  );
};

export default MessageContextMenu;